import type { ConnectEvent } from "~/lib/api";

/** Calendar day key in Australia/Sydney (YYYY-MM-DD). */
export function sydneyDayKey(d: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Australia/Sydney",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

/** Parse event_date to a Sydney calendar day key, or null if missing/invalid. */
export function eventDayKey(event: ConnectEvent): string | null {
  if (!event.event_date) return null;
  const parsed = new Date(event.event_date);
  if (Number.isNaN(parsed.getTime())) return null;
  return sydneyDayKey(parsed);
}

/**
 * Past = event calendar day is before today in Australia/Sydney.
 * Falls back to closes_at when event_date is missing (closed window → past).
 */
export function isPastConference(event: ConnectEvent, now: Date = new Date()): boolean {
  const today = sydneyDayKey(now);
  const day = eventDayKey(event);
  if (day) return day < today;
  if (event.closes_at) {
    const closes = new Date(event.closes_at);
    if (!Number.isNaN(closes.getTime())) return closes.getTime() < now.getTime();
  }
  return false;
}

function compareByEventDateAsc(a: ConnectEvent, b: ConnectEvent): number {
  const da = a.event_date ? Date.parse(a.event_date) : Number.POSITIVE_INFINITY;
  const db = b.event_date ? Date.parse(b.event_date) : Number.POSITIVE_INFINITY;
  if (da !== db) return da - db;
  return a.ticketing_event_id.localeCompare(b.ticketing_event_id);
}

function compareByEventDateDesc(a: ConnectEvent, b: ConnectEvent): number {
  return compareByEventDateAsc(b, a);
}

export type PartitionedEvents = {
  current: ConnectEvent[];
  past: ConnectEvent[];
};

/** Split Connect events into current tour (upcoming) vs past; sort appropriately. */
export function partitionConnectEvents(
  events: ConnectEvent[],
  now: Date = new Date(),
): PartitionedEvents {
  const current: ConnectEvent[] = [];
  const past: ConnectEvent[] = [];
  for (const event of events) {
    if (isPastConference(event, now)) past.push(event);
    else current.push(event);
  }
  current.sort(compareByEventDateAsc);
  past.sort(compareByEventDateDesc);
  return { current, past };
}

/** City + year when we have them, otherwise the event name. */
export function eventDisplayTitle(
  event: ConnectEvent | undefined,
  fallback = "Connect",
): string {
  if (!event) return fallback;
  if (event.city) {
    if (event.event_date) {
      const year = new Date(event.event_date).getFullYear();
      if (!Number.isNaN(year)) return `${event.city} ${year}`;
    }
    return event.city;
  }
  return event.name || fallback;
}

/** Short label for list rows, e.g. "16 Mar 2027". */
export function formatEventDateLabel(event: ConnectEvent): string | null {
  if (!event.event_date) return null;
  const parsed = new Date(event.event_date);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Sydney",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(parsed);
}
